import { type RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import { getFirstConsistentlyInteractive } from 'tti-polyfill';
import Cookies from 'js-cookie';
import { getAuthenticatedClient } from '@stitch-fix/client-facing-auth/browser';
import recordMark, { MARK_TIME_KEY } from '../recordMark';
import recordCustomTiming from '../recordCustomTiming';
import { Tags } from '../types';

declare global {
  interface Window {
    perfMetrics?: {
      onFirstInputDelay: (fn: (fid: number) => void) => void;
    };
  }
}

/**
 * Options for initializing performance tracking
 */
export type InitOptions = {
  /**
   * The Datadog RUM application ID
   */
  appId: string;
  /**
   * A Datadog client token
   */
  clientToken: string;
  /**
   * A human-readable name of the application (e.g. "kept-items-ui")
   */
  appName?: string;
  /**
   * The application's environment (e.g. "production", "development", etc.)
   */
  env?: string;
  /**
   * The application's version (typically the git SHA)
   */
  version?: string;
  /**
   * Percentage of sessions to track. Set to `0` to completely disable session
   * tracking. Set to `undefined` (or leave unspecified) for default. This value
   * is only respected in `production` environments. In non-production environments
   * it is set to 0 to avoid unnecessary reporting. See `debug` for information about
   * overriding this behavior for debugging purposes.
   */
  sampleRate?: number;
  /**
   * Data to include on the global context
   */
  tags?: Tags;
  /**
   * Set to `true` to enable debugging. By default, non-production environments
   * have a sample rate of zero, so that we do not report unnecessary data during
   * development or testing. Temporarily set to `true` to override this behavior,
   * sample data, and debug your reporting in non-production environments.
   */
  debug?: boolean;

  /**
   * this enables the connection of RUM and traces
   *
   * sources:
   *
   * - https://docs.datadoghq.com/real_user_monitoring/browser/#configuration:~:text=setup%20guide.-,allowedTracingUrls,-Optional%0AType%3A%20List
   * - https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
   * - https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm
   */
  allowedTracingUrls?: RumInitConfiguration['allowedTracingUrls'];
};

// Some URLs (such as PDPs) are not always automatically grouped by DataDog, because some product
// IDs do not have numeric characters, so we override the view name here.
// https://stitchfix.slack.com/archives/CJZQWEGR3/p1727451379509739
const viewNameOverrides = [
  ['/product/.*/.*', '/product/?/?'],
  ['/fix-home', '/fix-home/?'],
  ['/fix-home/.*', '/fix-home/?'],
  ['/orders/.*', '/orders/?'],
  ['/quiz/.*', '/quiz/?'],
];

const productionEnvironments = ['production', 'prod'];
const getIsProduction = (env?: string) =>
  productionEnvironments.includes(env || '');

export const beforeSend: NonNullable<
  Parameters<typeof datadogRum.init>[0]['beforeSend']
> = event => {
  if (event.type === 'error') {
    // this kind of error is common and can be safely ignored
    if (event.error.message.includes('ResizeObserver loop')) {
      return false;
    }
  }

  const { pathname } = new URL(event.view.url);

  for (const [regex, name] of viewNameOverrides) {
    if (pathname.match(regex)) {
      // eslint-disable-next-line no-param-reassign
      event.view.name = name;
    }
  }

  return true;
};

/**
 * Initializes performance tracking
 */
const init = ({
  allowedTracingUrls = ['https://www.stitchfix.com'],
  appId,
  clientToken,
  appName,
  env,
  version,
  sampleRate = 100,
  tags = {},
  debug,
}: InitOptions) => {
  const isProduction = getIsProduction(env);

  if (debug && !isProduction) {
    // eslint-disable-next-line no-console
    console.warn(
      'Client Analytics Reporter (CAR) is configured to debug, which will result in data being reported in non-production environments. When you are done debugging, you should disable this functionality by setting the `debug` option in the CAR initialization to `false` to avoid unnecessary reporting.',
    );
  }

  const shouldSampleSessions = isProduction || debug;

  const { client } = getAuthenticatedClient();
  const visitorId = Cookies.get('visitor_uuid');

  if (client) {
    datadogRum.setUser({
      id: client.clientId.toString(),
      email: client.email,
      visitor_id: visitorId,
    });
  } else if (visitorId) {
    datadogRum.setUser({
      id: visitorId,
      visitor_id: visitorId,
    });
  }

  if (Cookies.get('force_rum_session_replay')) {
    datadogRum.startSessionReplayRecording({ force: true });
  }

  // docs: https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters
  datadogRum.init({
    allowedTracingUrls,
    applicationId: appId,
    clientToken,
    site: 'datadoghq.com',
    service: appName,
    env,
    version,
    sessionSampleRate: shouldSampleSessions ? sampleRate : 0,
    sessionReplaySampleRate: 10,

    beforeSend,
  });

  datadogRum.startSessionReplayRecording({ force: true });

  for (const [tagName, tagValue] of Object.entries(tags)) {
    datadogRum.setGlobalContextProperty(tagName, tagValue);
  }

  // Record both for backwards compatibility with old alerts/dashboards.
  recordMark('init_performance_analytics');
  recordCustomTiming('init_performance_analytics');

  getFirstConsistentlyInteractive().then(
    tti => {
      if (typeof tti === 'number') {
        recordMark('time_to_interactive', { [MARK_TIME_KEY]: tti });
      }
    },
    () => {
      // do nothing if there's an error retrieving TTI
    },
  );

  // `perfMetrics` is created by the First Input Delay setup script. Don't call
  // the global if the setup script fails or if the object is not mocked in Jest
  // tests (e.g., in a parent application).
  if (window.perfMetrics && window.perfMetrics.onFirstInputDelay) {
    window.perfMetrics.onFirstInputDelay(fid => {
      recordMark('first_input_delay', { [MARK_TIME_KEY]: fid });
    });
  }
};

export default init;
